import { createEffect, onCleanup, untrack } from 'solid-js';

import {
  createGetPlayerSchedules,
  createHandleLiveProgress,
  createSchedulesCache,
  createUpdateSchedulesState,
  SchedulesState,
} from '@dtvgo/player-utils';
import { middleware } from '~/services/middleware';

import {
  addTimeUpdateListener,
  getLiveEdgeTimestamp,
  getLivePlaybackTimestamp,
} from '~/cast/lib';
import { createStore } from 'solid-js/store';

export function useSchedules(
  channelId: () => string,
  config: () =>
    | {
        retryCount: number;
        retryDelaySecs: number;
        fakeScheduleDefaultDurationMins: number;
        cacheSize: number;
        isDelayedWindowSecs?: number;
      }
    | undefined,
  initialStart: () => number | undefined = () => undefined,
) {
  const [state, setState] = createStore<SchedulesState>({
    schedules: { current: undefined, next: undefined },
    isLoading: false,
  });
  const currentScheduleEndTime = () =>
    untrack(() => state.schedules.current?.live.endTime);

  let updateState: ReturnType<typeof createUpdateSchedulesState> | undefined;

  createEffect(() => {
    const cfg = config();

    if (!cfg || updateState) return;

    const schedulesCache = createSchedulesCache(
      createGetPlayerSchedules(middleware, {
        pageSize: cfg.cacheSize,
        maxAttempts: cfg.retryCount + 1,
        delayBetweenAttempts: cfg.retryDelaySecs * 1000,
      }),
    );

    updateState = createUpdateSchedulesState(
      () => untrack(channelId),
      schedulesCache,
      setState,
      cfg.fakeScheduleDefaultDurationMins,
    );

    const handleProgress = createHandleLiveProgress(
      updateState,
      currentScheduleEndTime,
      cfg.isDelayedWindowSecs,
    );

    const removeTimeUpdateListener = addTimeUpdateListener(() => {
      handleProgress(getLivePlaybackTimestamp(), getLiveEdgeTimestamp());
    });
    onCleanup(removeTimeUpdateListener);
  });

  createEffect(() => {
    channelId(); // track channelId change
    const start = initialStart();
    updateState?.({
      currentTimestamp: start === undefined ? Date.now() : start * 1000,
      useNowNext: start === undefined,
      hardUpdate: true,
      startTime: start === undefined ? undefined : new Date(start * 1000),
    }).catch((error) => {
      console.error('useSchedules error', error);
      setState({
        schedules: { current: undefined, next: undefined },
        isLoading: false,
      });
    });
  });

  return [
    state,
    (...args: Parameters<ReturnType<typeof createUpdateSchedulesState>>) =>
      updateState?.(...args),
  ] as const;
}
